import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { get, entries } from 'lodash';
import { Dayjs } from 'dayjs';
import { Form, Button, Row, Col, Card, CardProps, Space, Spin, FormProps, Grid } from 'antd';
import { CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import { JsonParam, StringParam, useQueryParams, DateParam } from 'use-query-params';
import filterTypes, { FiltersMap } from './types';
import { convertToQP, convertFromQP } from './utils';

export interface FiltersProps {
  loading?: boolean;
  autoSubmit?: boolean;
  updateQuery?: boolean;
  filtersMap: FiltersMap;
  onValidSubmit: (params: any, headers: any) => void;
  onReset?: () => void;
  cardProps?: CardProps;
  extraBtns?: JSX.Element[];
}

export const queryParams = {
  fromDate: DateParam,
  toDate: DateParam,
  phrase: StringParam,
  filters: JsonParam,
  sums: JsonParam,
  // groups: JsonParam,
};

export type UseFiltersQuery = UseQP<typeof queryParams>;
export type FiltersFormData = {
  dateRange?: [Dayjs, Dayjs];
  phrase?: string;
} & {
  [key: string]: any;
};

function Filters(props: FiltersProps) {
  const {
    loading = false,
    autoSubmit = true,
    updateQuery = true,
    filtersMap,
    onValidSubmit,
    onReset,
    cardProps,
    extraBtns,
  } = props;

  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const location = useLocation();
  const autoSubmitted = useRef(false);
  const [query, setQuery] = useQueryParams(queryParams);

  const getInitialValues = useCallback(
    (newQuery: UseFiltersQuery['query']) => convertFromQP(newQuery, filtersMap),
    [filtersMap],
  );

  const [initialValues, setInitialValues] = useState(() => getInitialValues(query));

  useEffect(() => {
    if ((autoSubmit || location.state?.autoSubmit) && !autoSubmitted.current) {
      autoSubmitted.current = true;
      form.submit();
    }
  }, [autoSubmit, location, autoSubmitted, form]);
  const handleReset = useCallback(
    ({ fromHistory = false } = {}) => {
      setInitialValues(
        getInitialValues(
          fromHistory
            ? query
            : { toDate: null, fromDate: null, phrase: null, filters: {}, sums: null },
        ),
      );
      if (fromHistory) autoSubmitted.current = true;
      form.resetFields();
      setQuery(
        {
          fromDate: undefined,
          toDate: undefined,
          phrase: undefined,
          filters: undefined,
          sums: undefined,
          // groups: undefined,
          sortBy: undefined,
          sortOrder: undefined,
          page: undefined,
          pageSize: undefined,
        } as any,
        'pushIn',
      );
      if (onReset && !fromHistory) onReset();
      form.submit();
    },
    [getInitialValues, query, form, onReset, setQuery],
  );

  const handleSubmit = useCallback<NonNullable<FormProps['onFinish']>>(
    (data) => {
      const { apiHeaders, apiParams } = convertToQP(setQuery, data, query, {
        updateQuery,
        defaults: autoSubmitted.current,
      });
      onValidSubmit(apiParams, apiHeaders);
    },
    [query, setQuery, updateQuery, onValidSubmit],
  );

  // Submit failed
  const handleFinishFailed = useCallback<NonNullable<FormProps['onFinishFailed']>>(
    ({ errorFields }) => {
      if (!errorFields[0]?.name) return;
      form.scrollToField(errorFields[0].name);
    },
    [form],
  );

  const FilterItems = useMemo(
    () =>
      entries(filterTypes).map(([type, { component: render }], index) => {
        let filterProps: any = filtersMap[type as keyof FiltersMap];
        if (filterProps === true || (type === 'date' && filterProps === undefined)) {
          filterProps = {};
        }
        if (!filterProps) return null;

        const initialValKey =
          type === 'date'
            ? 'dateRange'
            : ['filters', 'name' in filterProps && filterProps.name ? filterProps.name : type];
        filterProps.initialValue = get(initialValues, initialValKey);

        // @ts-ignore
        return render({ ...filterProps, key: String(index) });
      }),
    [filtersMap, initialValues],
  );

  if (!initialValues) return null;

  return (
    <Card className="filters" {...cardProps}>
      <Form
        form={form}
        className="filter-form"
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleFinishFailed}
        initialValues={initialValues}
      >
        <Row gutter={20}>{FilterItems}</Row>

        <Row gutter={[12, 12]} className="filters-action-row">
          <Col xs={24} md={12}>
            <Space size="middle" wrap className="filters-primary-actions">
              <Button
                type="primary"
                htmlType="reset"
                danger
                size="large"
                icon={<ReloadOutlined />}
                disabled={loading}
                onClick={() => handleReset()}
              >
                Reset
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                icon={<CheckOutlined />}
                disabled={loading}
              >
                View
              </Button>
              {loading ? <Spin /> : null}
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space
              size="middle"
              wrap
              className="filters-extra-actions"
              style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}
            >
              {extraBtns?.map((btn, index) => (
                <React.Fragment key={`extra-btn-${index}`}>{btn}</React.Fragment>
              ))}
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}

export default Filters;
